/**
 * Custom hook for managing user topics data with selection state
 */

import { useState, useEffect } from 'react';
import { getUserTopics } from '../services/topicService';
import type { UserTopic } from '@/types/topic';

interface UseUserTopicsReturn {
  userTopics: UserTopic[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch and manage user topics with selection state and UI enhancements
 * @returns Object containing user topics data, loading state, error state, and refetch function
 */
export const useUserTopics = (): UseUserTopicsReturn => {
  const [userTopics, setUserTopics] = useState<UserTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserTopics = async () => {
    try {
      console.log('🔄 [useUserTopics] Starting to fetch user topics...');
      setLoading(true);
      setError(null);

      const response = await getUserTopics();
      console.log('📥 [useUserTopics] Response received from service:', {
        success: response.success,
        dataCount: response.data.length,
        message: response.message
      });

      if (response.success) {
        console.log('✅ [useUserTopics] User topics successfully processed:', response.data);
        setUserTopics(response.data);
      } else {
        console.log('❌ [useUserTopics] API returned error:', response.message);
        setError(response.message || 'Failed to load user topics');
      }
    } catch (err) {
      console.error('🚨 [useUserTopics] Unexpected error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      console.log('🏁 [useUserTopics] Fetch process completed, loading state set to false');
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchUserTopics();
  };

  useEffect(() => {
    fetchUserTopics();
  }, []);

  return {
    userTopics,
    loading,
    error,
    refetch
  };
};
